import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined";
import TodayIcon from "@mui/icons-material/Today";
import StarRateIcon from "@mui/icons-material/StarRate";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveIcon from "@mui/icons-material/Remove";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SpeedIcon from "@mui/icons-material/Speed";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CategoryIcon from "@mui/icons-material/Category";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import GroupIcon from "@mui/icons-material/Group";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import LanguageIcon from "@mui/icons-material/Language";
import ConnectedTvRoundedIcon from "@mui/icons-material/ConnectedTvRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import PersonIcon from "@mui/icons-material/Person";
import NotesIcon from "@mui/icons-material/Notes";
import EditIcon from "@mui/icons-material/Edit";
import { LiveTvFilledIcon } from "../components/icons";
import { toast } from "react-toastify";

export const ratingLabels = {
  0.5: "atrocious",
  1: "very poor",
  1.5: "poor",
  2: "disappointing",
  2.5: "mediocre",
  3: "good",
  3.5: "very good",
  4: "great",
  4.5: "exceptional",
  5: "favourite",
};

// Formats a true instant/timestamp (a UTC moment, e.g. an account-created or
// status-marked time) as 'dd mmm yyyy' in the viewer's LOCAL timezone. For dates that
// represent a calendar day with no time (journal/release dates, "YYYY-MM-DD"), use
// formatCalendarDate instead — local formatting would shift the day for viewers off UTC.
export const formatDateFromTimestamp = (dateString, format) => {
  const date = new Date(dateString);
  if (format === "dropDay") {
    return date.toLocaleDateString("en-GB", {
      month: "short",
      year: "numeric",
    });
  }
  if (format === "dropYear") {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  }
  if (format === "shortYear") {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Formats a timezone-agnostic calendar date (e.g. a journal finish date or a release
// date stored as "YYYY-MM-DD") by its UTC calendar day, so the rendered day never
// shifts for viewers off UTC. Use this for bare date fields; use formatDateFromTimestamp
// for true timestamps, where local formatting is the desired behaviour.
export const formatCalendarDate = (dateString, format) => {
  const date = new Date(dateString);
  const tz = { timeZone: "UTC" };
  if (format === "dropDay") {
    return date.toLocaleDateString("en-GB", {
      ...tz,
      month: "short",
      year: "numeric",
    });
  }
  if (format === "dropYear") {
    return date.toLocaleDateString("en-GB", {
      ...tz,
      day: "2-digit",
      month: "short",
    });
  }
  if (format === "shortYear") {
    return date.toLocaleDateString("en-GB", {
      ...tz,
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  }
  return date.toLocaleDateString("en-GB", {
    ...tz,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// UTC calendar-day key ("YYYY-MM-DD") for comparing a bare date against a timestamp on
// the same basis used by formatCalendarDate. Returns null for falsy input.
export const utcDayKey = (value) =>
  value ? new Date(value).toISOString().slice(0, 10) : null;

// Returns the calendar year of a (possibly bare) date. Uses the UTC year so a date like
// "2024-01-01" doesn't read as 2023 for viewers west of UTC (new Date parses bare dates
// at UTC midnight).
export const getYear = (date) => {
  return date ? new Date(date).getUTCFullYear() : null;
};

export const getContentOptions = ({ showEverythingOption = false } = {}) => {
  const options = {
    movie: {
      labelSingular: "Movie",
      labelPlural: "Movies",
      icon: <MovieRoundedIcon />,
      disabled: false,
      slug: "movies",
      routeSlug: "movie",
    },
    tvshow: {
      labelSingular: "TV show",
      labelPlural: "TV shows",
      icon: <LiveTvFilledIcon />,
      disabled: false,
      preserveCase: true,
      slug: "tv",
      routeSlug: "tv",
    },
    book: {
      labelSingular: "Book",
      labelPlural: "Books",
      icon: <MenuBookRoundedIcon />,
      disabled: false,
      slug: "books",
      routeSlug: "book",
    },
    // podcast: {
    //   labelSingular: "Podcast",
    //   labelPlural: "Podcasts",
    //   icon: <PodcastsRoundedIcon />,
    //   disabled: true,
    // },
    // Always included for icon/label lookups (e.g. collection covers), but hidden from
    // general content-type pickers — use showMixedOption in getContentOptionsArray to expose it.
    mixed: {
      labelSingular: "Mixed",
      labelPlural: "Mixed",
      icon: <CategoryIcon />,
      disabled: false,
      collectionOnly: true,
    },
  };

  if (showEverythingOption) {
    return {
      all: {
        labelSingular: "All",
        labelPlural: "All",
        icon: <AppsRoundedIcon />,
        disabled: false,
      },
      ...options,
    };
  }

  return options;
};

export const getContentOptionsArray = ({
  showEverythingOption = false,
  showMixedOption = false,
} = {}) =>
  Object.entries(getContentOptions({ showEverythingOption }))
    .filter(([, option]) => showMixedOption || !option.collectionOnly)
    .map(([value, option]) => ({ value, ...option }));

// Content type options (no "All") ordered by `counts` (value → number),
// highest first; ties keep the default order. For sections that show a
// person's types most-tracked first (the profile's top 5s and stats).
export const orderContentTypesByCount = (counts = {}) =>
  getContentOptionsArray().sort(
    (a, b) => (counts[b.value] ?? 0) - (counts[a.value] ?? 0),
  );

// Map a URL slug (e.g. "movies") back to its content-type value ("movie").
// Returns undefined for unknown slugs. Used when parsing content type out of a route.
export const getContentTypeFromSlug = (slug) =>
  Object.entries(getContentOptions()).find(
    ([, option]) => option.slug === slug,
  )?.[0];

// Map a content-type value ("movie") to its URL slug ("movies").
// Used when building explore/track route paths from an enum value.
export const getContentTypeSlug = (contentType) =>
  getContentOptions()[contentType]?.slug;

// A Track page path: /track/u/:username/:contentType/:status, or with the
// content type segment left out for All (as Explore does) — getContentTypeSlug
// has no slug for "all".
export const getTrackPath = (username, contentType, status) => {
  const contentSlug = getContentTypeSlug(contentType);
  return `/track/u/${username}${contentSlug ? `/${contentSlug}` : ""}/${getStatusSlug(status)}`;
};

// Lowercase noun for a content type in running text ("no books match…"), with
// "things" for All, which has no label of its own that reads in a sentence.
export const getContentNoun = ({ type, plural = false }) =>
  type === "all"
    ? plural
      ? "things"
      : "thing"
    : getContentLabel({ type, plural, lowercase: true });

// Map a detail-route slug ("movie"|"tv"|"book") back to its content-type value.
// Distinct from getContentTypeFromSlug's plural filter slugs — used for the
// singular /movie|tv|book/:contentId detail routes.
export const getContentTypeFromRouteSlug = (routeSlug) =>
  Object.entries(getContentOptions()).find(
    ([, option]) => option.routeSlug === routeSlug,
  )?.[0];

// Map a content-type value ("movie") to its detail-route slug ("movie"|"tv"|"book").
export const getContentTypeRouteSlug = (contentType) =>
  getContentOptions()[contentType]?.routeSlug;

export function getContentLabel({ type, plural = false, lowercase = false }) {
  const content = getContentOptions()[type];
  if (!content) return "";

  const label = plural ? content.labelPlural : content.labelSingular;
  return content.preserveCase ? label : lowercase ? label.toLowerCase() : label;
}

// Irregular plurals that the regex rules below can't derive correctly.
// Keyed by lowercase singular; add an entry here if a new admin-entered role
// (or other pluralized label) doesn't follow the standard English rules.
const IRREGULAR_PLURALS = {
  person: "people",
};

// Pluralizes a singular English noun/phrase for display (e.g. tag display names).
// Handles the common regular cases — consonant+y -> ies, s/x/z/ch/sh -> es,
// otherwise +s — plus an exceptions map for irregular words. Only pluralizes the
// last word so multi-word phrases like "media personality" pluralize correctly.
export function pluralize(word) {
  if (!word) return word;

  const lastSpace = word.lastIndexOf(" ");
  const prefix = lastSpace === -1 ? "" : word.slice(0, lastSpace + 1);
  const lastWord = lastSpace === -1 ? word : word.slice(lastSpace + 1);

  const irregular = IRREGULAR_PLURALS[lastWord.toLowerCase()];
  if (irregular) return prefix + irregular;

  if (/[^aeiou]y$/i.test(lastWord)) {
    return prefix + lastWord.slice(0, -1) + "ies";
  }
  if (/(s|x|z|ch|sh)$/i.test(lastWord)) {
    return prefix + lastWord + "es";
  }
  return prefix + lastWord + "s";
}

export const getStatusOptions = (theme = null) => ({
  queued: {
    label: "Queued",
    icon: (
      <WatchLaterOutlinedIcon
        sx={{ color: (theme) => theme.palette.status.queued.main }}
      />
    ),
    iconWhite: <WatchLaterOutlinedIcon />,
    iconFilled: (
      <WatchLaterIcon
        sx={{ color: (theme) => theme.palette.status.queued.main }}
      />
    ),
    color: theme ? theme.palette.status.queued.main : undefined,
    contrastText: theme ? theme.palette.status.queued.contrastText : undefined,
    action: "Queue",
  },
  started: {
    label: "Started",
    icon: (
      <PlayCircleOutlineIcon
        sx={{ color: (theme) => theme.palette.status.started.main }}
      />
    ),
    iconWhite: <PlayCircleOutlineIcon />,
    iconFilled: (
      <PlayCircleIcon
        sx={{ color: (theme) => theme.palette.status.started.main }}
      />
    ),
    color: theme ? theme.palette.status.started.main : undefined,
    contrastText: theme ? theme.palette.status.started.contrastText : undefined,
    action: "Start",
  },
  abandoned: {
    label: "Stopped",
    // Track URLs use "stopped" rather than the raw enum value — see
    // getStatusSlug/getStatusFromSlug below.
    slug: "stopped",
    icon: (
      <RemoveCircleOutlineIcon
        sx={{ color: (theme) => theme.palette.status.abandoned.main }}
      />
    ),
    iconWhite: <RemoveCircleOutlineIcon />,
    iconFilled: (
      <RemoveCircleIcon
        sx={{ color: (theme) => theme.palette.status.abandoned.main }}
      />
    ),
    color: theme ? theme.palette.status.abandoned.main : undefined,
    contrastText: theme
      ? theme.palette.status.abandoned.contrastText
      : undefined,
    action: "Stop",
  },
  finished: {
    label: "Finished",
    icon: (
      <CheckCircleOutlineIcon
        sx={{ color: (theme) => theme.palette.status.finished.main }}
      />
    ),
    iconWhite: <CheckCircleOutlineIcon />,
    iconFilled: (
      <CheckCircleIcon
        sx={{ color: (theme) => theme.palette.status.finished.main }}
      />
    ),
    iconWhiteFilled: <CheckCircleIcon />,
    color: theme ? theme.palette.status.finished.main : undefined,
    contrastText: theme
      ? theme.palette.status.finished.contrastText
      : undefined,
    action: "Finish",
  },
  // logged: {
  //   label: "Logged",
  //   icon: <CheckCircleIcon />,
  //   color: theme ? theme.palette.success.light : undefined,
  //   action: "Log",
  // },
  none: {
    label: "Add",
    icon: <AddIcon />,
    color: theme ? theme.palette.primary : undefined,
    action: "Add",
  },
});

export const getStatusOptionsArray = (theme) =>
  Object.entries(getStatusOptions(theme)).map(([value, option]) => ({
    value,
    ...option,
  }));

// Map a status value ("abandoned") to its track URL slug ("stopped"). Statuses
// without a distinct slug fall back to their own value, so callers can apply
// this unconditionally when building a track URL.
export const getStatusSlug = (status) =>
  getStatusOptions()[status]?.slug ?? status;

// Map a track URL's status slug ("stopped") back to the internal status value
// ("abandoned"). Falls back to treating the slug as the value itself, so
// legacy links carrying the old raw value still resolve.
export const getStatusFromSlug = (slug) =>
  Object.entries(getStatusOptions()).find(
    ([value, option]) => (option.slug ?? value) === slug,
  )?.[0] ?? slug;

// `defaultDescending` is the direction a sort opens in when first selected.
// `toggleLabel` is the text shown beside the direction switch in SortMenu.
// `groupsByMonth`: a sort by a moment in time (log, finish or edit date), whose
// list Track can divide into months — not Year, a release year
export const getSortByOptions = (status) => [
  {
    label: "Finish date",
    value: "finish_date",
    icon: <EventAvailableIcon fontSize="small" />,
    disabled: status !== "finished",
    defaultDescending: true,
    toggleLabel: "Most recent first",
    groupsByMonth: true,
  },
  {
    label: "Log date",
    value: "log_date",
    icon: <TodayIcon fontSize="small" />,
    disabled: false,
    defaultDescending: true,
    toggleLabel: "Most recent first",
    groupsByMonth: true,
  },
  {
    // An entry's last change (rating, note, reactions...) — finished only,
    // as other tabs' statuses carry no edits
    label: "Edit date",
    value: "edited",
    icon: <EditIcon fontSize="small" />,
    disabled: status !== "finished",
    defaultDescending: true,
    toggleLabel: "Most recent first",
    groupsByMonth: true,
  },
  {
    label: "Rating",
    value: "rating",
    icon: <StarRateIcon fontSize="small" />,
    disabled: status !== "finished",
    defaultDescending: true,
    toggleLabel: "Highest rated first",
  },
  {
    label: "Title",
    value: "title",
    icon: <SortByAlphaIcon fontSize="small" />,
    disabled: false,
    defaultDescending: false,
    toggleLabel: "A to Z",
  },
  {
    label: "Year",
    value: "year",
    icon: <CalendarMonthIcon fontSize="small" />,
    disabled: false,
    defaultDescending: true,
    toggleLabel: "Newest first",
  },
];

// Every Track filter. Each `value` is held exactly as its URL token (e.g. a
// rating of "3-4.5"); the backend handler of the same paramKey documents the
// format. `dynamic` filters take their options from the /content/filter-options
// response (keyed by `type`) rather than a fixed list. `label` may be a
// function of the content type. `toggle` filters have a single
// value, applied straight from the Filter menu with no menu of their own.
export const FILTER_CONFIG = [
  {
    type: "Provider",
    label: "Service",
    paramKey: "providers",
    icon: <ConnectedTvRoundedIcon />,
    // Books have no streaming availability; All filters its movies and TV
    disabled: (status, contentType) => contentType === "book",
    serialize: (value) => value.join(","),
    deserialize: (str) => str.split(",").map(Number),
    isArray: true,
    dynamic: true,
    dividerAfter: true,
  },
  {
    type: "Genre",
    paramKey: "genres",
    icon: <TheaterComedyIcon />,
    // Genre lists are per content type, so All has none to offer
    disabled: (status, contentType) => contentType === "all",
    serialize: (value) => value.join(","),
    deserialize: (str) => str.split(","),
    isArray: true,
  },
  {
    type: "Year",
    label: "Release year",
    paramKey: "years",
    icon: <CalendarMonthIcon />,
    disabled: () => false,
    // Years and/or ranges ("1999,2004", "1990..1999" for a decade, "1985..2005")
    serialize: (value) => value,
    deserialize: (str) => str,
    isArray: false,
    dynamic: true,
  },
  {
    type: "Language",
    paramKey: "languages",
    icon: <LanguageIcon />,
    disabled: () => false,
    serialize: (value) => value.join(","),
    deserialize: (str) => str.split(","),
    isArray: true,
    dynamic: true,
  },
  {
    type: "Person",
    // Whoever the type credits: directors, TV creators, authors
    label: (contentType) =>
      ({ movie: "Director", tvshow: "Creator", book: "Author" })[contentType] ??
      "Person",
    paramKey: "people",
    icon: <PersonIcon />,
    disabled: () => false,
    serialize: (value) => value.join(","),
    deserialize: (str) => str.split(",").map(Number),
    isArray: true,
    dynamic: true,
    dividerAfter: true,
  },
  {
    type: "Rating",
    paramKey: "rating",
    icon: <StarRateIcon />,
    disabled: (status, contentType) => status !== "finished",
    serialize: (value) => value,
    deserialize: (str) => str,
    isArray: false,
  },
  {
    type: "Finished",
    label: "Finish date",
    paramKey: "finished",
    icon: <EventAvailableIcon />,
    disabled: (status, contentType) => status !== "finished",
    serialize: (value) => value,
    deserialize: (str) => str,
    isArray: false,
  },
  {
    type: "Note",
    paramKey: "note",
    icon: <NotesIcon />,
    disabled: (status, contentType) => status !== "finished",
    serialize: (value) => value,
    deserialize: (str) => str,
    isArray: false,
  },
  {
    type: "Reaction",
    paramKey: "reactions",
    icon: <EmojiEmotionsIcon />,
    disabled: (status, contentType) => status !== "finished",
    serialize: (value) => value.join(","),
    deserialize: (str) => str.split(","),
    isArray: true,
  },
  {
    type: "Rewatch",
    // All mixes types, so it gets a word that fits films, TV and books alike
    label: (contentType) =>
      ({ book: "Reread", all: "Revisit" })[contentType] ?? "Rewatch",
    paramKey: "rewatch",
    icon: <RefreshIcon />,
    disabled: (status, contentType) => status !== "finished",
    serialize: (value) => value,
    deserialize: (str) => str,
    isArray: false,
    toggle: true,
  },
];

export const getFilterOptions = (status, contentType) =>
  FILTER_CONFIG.map((f) => ({
    label:
      typeof f.label === "function"
        ? f.label(contentType)
        : (f.label ?? f.type),
    value: f.type,
    icon: f.icon,
    disabled: f.disabled(status, contentType),
    dividerAfter: f.dividerAfter ?? false,
  }));

export const getLanguageLabel = (isoCode) => {
  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
    return displayNames.of(isoCode) ?? isoCode;
  } catch {
    return isoCode;
  }
};

/** Options for the Curate page header view switcher (my collections vs. saved). */
export const getListViewOptions = () => [
  {
    label: "My collections",
    value: "mine",
    icon: <CollectionsBookmarkIcon fontSize="small" />,
    disabled: false,
  },
  {
    label: "Saved collections",
    value: "saved",
    icon: <BookmarksIcon fontSize="small" />,
    disabled: false,
  },
];

export const REACTION_OPTIONS = [
  { label: "slow-paced", category: "Pacing", rank: 1, icon: <SpeedIcon /> },
  { label: "medium-paced", category: "Pacing", rank: 1, icon: <SpeedIcon /> },
  { label: "fast-paced", category: "Pacing", rank: 1, icon: <SpeedIcon /> },
  { label: "cute", category: "Reactions", rank: 1, emoji: "😍" },
  { label: "aesthetic", category: "Reactions", rank: 1, emoji: "🎨" },
  { label: "funny", category: "Reactions", rank: 1, emoji: "😂" },
  { label: "infuriating", category: "Reactions", rank: 1, emoji: "😡" },
  { label: "worrying", category: "Reactions", rank: 1, emoji: "😟" },
  { label: "awe-inspiring", category: "Reactions", rank: 1, emoji: "😲" },
  { label: "awkward", category: "Reactions", rank: 1, emoji: "😳" },
  { label: "boring", category: "Reactions", rank: 1, emoji: "😴" },
  { label: "chill", category: "Reactions", rank: 1, emoji: "😎" },
  { label: "confusing", category: "Reactions", rank: 1, emoji: "🤯" },
  { label: "disgusting", category: "Reactions", rank: 1, emoji: "🤢" },
  { label: "distressing", category: "Reactions", rank: 1, emoji: "😖" },
  { label: "gripping", category: "Reactions", rank: 1, emoji: "🤩" },
  { label: "exciting", category: "Reactions", rank: 1, emoji: "😄" },
  { label: "scary", category: "Reactions", rank: 1, emoji: "😱" },
  { label: "disturbing", category: "Reactions", rank: 1, emoji: "😨" },
  { label: "interesting", category: "Reactions", rank: 1, emoji: "🤔" },
  { label: "happy", category: "Reactions", rank: 1, emoji: "😊" },
  { label: "nostalgic", category: "Reactions", rank: 1, emoji: "😶‍🌫️" },
  { label: "comforting", category: "Reactions", rank: 1, emoji: "😌" },
  { label: "romantic", category: "Reactions", rank: 1, emoji: "🥰" },
  { label: "sad", category: "Reactions", rank: 1, emoji: "😢" },
  { label: "satisfying", category: "Reactions", rank: 1, emoji: "😌" },
  { label: "sexual", category: "Reactions", rank: 1, emoji: "😛" },
  { label: "surprising", category: "Reactions", rank: 1, emoji: "😮" },
  {
    label: "acting",
    category: "Kudos",
    rank: 1,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "writing",
    category: "Kudos",
    rank: 2,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "directing",
    category: "Kudos",
    rank: 4,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "soundtrack",
    category: "Kudos",
    rank: 6,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "editing",
    category: "Kudos",
    rank: 7,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "vfx",
    category: "Kudos",
    rank: 8,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "sound design",
    category: "Kudos",
    rank: 10,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "costume design",
    category: "Kudos",
    rank: 11,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "set design",
    category: "Kudos",
    rank: 12,
    emoji: "👏",
    contentTypes: ["movie", "tvshow"],
  },
  {
    label: "writing style",
    category: "Kudos",
    rank: 1,
    emoji: "👏",
    contentTypes: ["book"],
  },
  {
    label: "story",
    category: "Kudos",
    rank: 2,
    emoji: "👏",
    contentTypes: ["book"],
  },
  {
    label: "character development",
    category: "Kudos",
    rank: 3,
    emoji: "👏",
    contentTypes: ["book"],
  },
  {
    label: "worldbuilding",
    category: "Kudos",
    rank: 4,
    emoji: "👏",
    contentTypes: ["book"],
  },
  {
    label: "dialogue",
    category: "Kudos",
    rank: 6,
    emoji: "👏",
    contentTypes: ["book"],
  },
  {
    label: "themes & ideas",
    category: "Kudos",
    rank: 7,
    emoji: "👏",
    contentTypes: ["book"],
  },
  {
    label: "research & accuracy",
    category: "Kudos",
    rank: 7,
    emoji: "👏",
    contentTypes: ["book"],
  },
];

// Reactions offered for a content type; All offers every reaction
export const getFilteredReactionOptions = (contentType) => {
  return REACTION_OPTIONS.filter((opt) => {
    if (!opt.contentTypes || contentType === "all") return true;
    return opt.contentTypes.includes(contentType);
  }).sort((a, b) => {
    const order = { Pacing: 1, Kudos: 2, Reactions: 3 };
    return order[a.category] - order[b.category];
  });
};

const MOVIE_GENRES = [
  "Action",
  "Adventure",
  "Animation",
  "Comedy",
  "Crime",
  "Documentary",
  "Drama",
  "Family",
  "Fantasy",
  "History",
  "Horror",
  "Music",
  "Mystery",
  "Romance",
  "Science Fiction",
  "Thriller",
  "TV Movie",
  "War",
  "Western",
];

const TVSHOW_GENRES = [
  "Action & Adventure",
  "Animation",
  "Comedy",
  "Crime",
  "Documentary",
  "Drama",
  "Family",
  "Kids",
  "Musical",
  "Mystery",
  "News",
  "Reality",
  "Romance",
  "Sci-Fi & Fantasy",
  "Soap",
  "Talk",
  "War & Politics",
  "Western",
];

const FICTION_BOOK_GENRES = [
  "Fantasy",
  "Sci Fi",
  "Historical Fiction",
  "Mystery",
  "Thriller",
  "Horror",
  "Romance",
  "Literary Fiction",
  "Magical Realism",
  "Adventure",
  "Crime",
  "Comedy",
  "Queer",
  "Poetry",
  "Short Story",
  "Comic/Graphic Novel",
  "Play",
  "Anthology",
];

const NONFICTION_BOOK_GENRES = [
  "Art & Design",
  "Biography",
  "Business & Economics",
  "Education",
  "Food",
  "History",
  "Humour",
  "Music",
  "Nature & Environment",
  "Self Help",
  "Politics",
  "Psychology",
  "Religion & Spirituality",
  "Science & Technology",
  "Sports",
  "Travel",
  "True Crime",
  "Wellness",
];

export const getGenreOptions = ({ type, subtype = "all" }) => {
  if (type === "movie") {
    return MOVIE_GENRES.sort();
  }

  if (type === "tvshow") {
    return TVSHOW_GENRES.sort();
  }

  if (type === "book") {
    if (subtype === "fiction") {
      return FICTION_BOOK_GENRES.sort();
    }

    if (subtype === "nonfiction") {
      return NONFICTION_BOOK_GENRES.sort();
    }

    return {
      Fiction: FICTION_BOOK_GENRES.sort(),
      "Non-fiction": NONFICTION_BOOK_GENRES.sort(),
    };
  }

  return [];
};

export const tileHoverOptions = [
  {
    label: "Replace",
    value: "replace",
    icon: <RefreshIcon />,
  },
  {
    label: "Remove",
    value: "remove",
    icon: <RemoveIcon />,
  },
  {
    label: "Open",
    value: "open",
    icon: <OpenInFullIcon />,
  },
];

export const visibilityOptions = [
  {
    value: "only_me",
    label: "Only me",
    icon: <LockIcon />,
  },
  {
    value: "followers",
    label: "Followers",
    icon: <GroupIcon />,
  },
  {
    value: "everyone",
    label: "Everyone",
    icon: <PublicIcon />,
  },
];

// Coarse relative time. Runs all the way out to years because it labels events
// that can be arbitrarily old (e.g. when someone finished a film), where
// "148 weeks ago" would be unreadable. Singulars are spelled out so a count of
// one never reads "1 months ago".
//
// `short` is for tight spaces like poster captions: "25m ago", "3h ago",
// "2d ago", "3w ago", "4mo ago", "2y ago" — "m" for minutes and "mo" for
// months, as most apps abbreviate them.
export const getTimeAgo = (isoString, { short = false } = {}) => {
  const timestamp = new Date(isoString);
  const now = new Date();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (short) {
    if (diffMins < 10) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${diffWeeks}w ago`;
    if (diffDays < 365) return `${diffMonths}mo ago`;
    return `${diffYears}y ago`;
  }

  if (diffMins < 10) return "a few mins ago";
  if (diffMins < 60) return `${diffMins} mins ago`;
  if (diffHours < 24)
    return diffHours === 1 ? "an hour ago" : `${diffHours} hours ago`;
  if (diffDays < 7)
    return diffDays === 1 ? "yesterday" : `${diffDays} days ago`;
  if (diffDays < 30)
    return diffWeeks === 1 ? "a week ago" : `${diffWeeks} weeks ago`;
  if (diffDays < 365)
    return diffMonths === 1 ? "a month ago" : `${diffMonths} months ago`;
  return diffYears === 1 ? "a year ago" : `${diffYears} years ago`;
};

export function handleApiError(error) {
  // Network error → no response object. Persistent toast since the user remains
  // offline until they manually retry — auto-dismissing would be misleading.
  // toastId prevents duplicate toasts when multiple queries fail simultaneously.
  if (!error.response) {
    toast.error("You are currently offline", {
      // autoClose: false,
      toastId: "offline",
    });
    return;
  }

  const data = error.response.data;

  // User-visible backend error
  if (data?.isUserVisible) {
    toast.error(data.message, {
      ...(data.persist ? { autoClose: false } : {}),
    });
    return;
  }

  // Internal server error (5xx) or other unexpected errors
  toast.error("Something went wrong, please try again later");
}

export const safeMutate = (mutateAsync) => {
  // React Query already runs the global defaultOptions.mutations.onError handler
  // when a mutation fails, so all user-facing error handling (toasts, UI messages)
  // is taken care of at the framework level.
  //
  // However, mutateAsync() also returns a real JavaScript Promise that *rejects*
  // on mutation failure. This rejection is separate from React Query’s internal
  // error handling. If the caller uses `await mutateAsync()`, that rejected
  // Promise will bubble up the component tree as an unhandled promise rejection,
  // which React treats as a runtime error (showing the red error overlay).
  //
  // To avoid forcing callers to wrap every awaited mutation in try/catch or
  // `.catch()`, we centralise that behaviour here. `safeMutate` wraps the
  // Promise-returning mutateAsync function, swallows the rejection, and leaves
  // React Query’s built-in error pipeline to handle user notifications.
  //
  // This achieves three things:
  //   1. Prevents unhandled promise rejections in async event flows.
  //   2. Keeps all mutation call sites clean (`await mutate(...)` with no boilerplate).
  //   3. Ensures all UX error handling remains centralised in one global place.
  //
  // This is the recommended pattern when exposing mutateAsync to UI layers: allow
  // React Query to own the UX error behaviour, and prevent JavaScript from
  // crashing the component tree when an awaited mutation fails.
  return (args) => mutateAsync(args).catch(() => {});
};

const BOOK_COVER_PALETTE = [
  "799BA2",
  "#8BAEC9", // muted blue
  "#5E8287", // mid blue
  "#033C46", // soft sage-teal
  "#B9D8C9", // muted teal
  "#69948C", // deeper teal
  "#C7CBCD", // neutral grey
  "#AEB4B7", // mid grey
  "#969DA1", // deeper grey
];

export function getBookColor(title = "") {
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = title.charCodeAt(i) + ((hash << 5) - hash);
  }
  return BOOK_COVER_PALETTE[Math.abs(hash) % BOOK_COVER_PALETTE.length];
}

export function stringToColor(string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

export function stringToBrandColor(str = "") {
  // Deterministic hash
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const abs = Math.abs(hash);

  // --- 1. Curate-safe hue bands ---
  const hueBands = [
    // Teal / turquoise band
    [160, 190],
    // Blue / slate band
    [190, 220],
    // Pale purple band
    [250, 275],
  ];

  // Flatten into N evenly-spaced hue buckets
  const BUCKETS_PER_BAND = 8; // tweak for even more variation
  const hues = [];

  for (const [min, max] of hueBands) {
    const step = (max - min) / BUCKETS_PER_BAND;
    for (let i = 0; i < BUCKETS_PER_BAND; i++) {
      hues.push(min + i * step);
    }
  }

  // --- 2. Pick hue deterministically from hash ---
  const hue = hues[abs % hues.length];

  // --- 3. Controlled saturation and lightness ---
  const SAT_MIN = 18;
  const SAT_MAX = 45;

  const LIGHT_MIN = 35;
  const LIGHT_MAX = 55;

  const sat = SAT_MIN + ((abs >> 7) % (SAT_MAX - SAT_MIN));
  let light = LIGHT_MIN + ((abs >> 15) % (LIGHT_MAX - LIGHT_MIN));

  // --- 4. Ensure white-text readability ---
  if (light > 60) light = 55;

  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

export const formatCount = (n) => {
  if (n == null) return "—";
  if (n === 0) return "—";
  const formatted = new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
  return formatted.replace("K", "k").replace("M", "m").replace("B", "b");
};

export const COUNTRIES = [
  { code: "AD", label: "Andorra", phone: "376" },
  {
    code: "AE",
    label: "United Arab Emirates",
    phone: "971",
  },
  { code: "AF", label: "Afghanistan", phone: "93" },
  {
    code: "AG",
    label: "Antigua and Barbuda",
    phone: "1-268",
  },
  { code: "AI", label: "Anguilla", phone: "1-264" },
  { code: "AL", label: "Albania", phone: "355" },
  { code: "AM", label: "Armenia", phone: "374" },
  { code: "AO", label: "Angola", phone: "244" },
  { code: "AQ", label: "Antarctica", phone: "672" },
  { code: "AR", label: "Argentina", phone: "54" },
  { code: "AS", label: "American Samoa", phone: "1-684" },
  { code: "AT", label: "Austria", phone: "43" },
  {
    code: "AU",
    label: "Australia",
    phone: "61",
  },
  { code: "AW", label: "Aruba", phone: "297" },
  { code: "AX", label: "Alland Islands", phone: "358" },
  { code: "AZ", label: "Azerbaijan", phone: "994" },
  {
    code: "BA",
    label: "Bosnia and Herzegovina",
    phone: "387",
  },
  { code: "BB", label: "Barbados", phone: "1-246" },
  { code: "BD", label: "Bangladesh", phone: "880" },
  { code: "BE", label: "Belgium", phone: "32" },
  { code: "BF", label: "Burkina Faso", phone: "226" },
  { code: "BG", label: "Bulgaria", phone: "359" },
  { code: "BH", label: "Bahrain", phone: "973" },
  { code: "BI", label: "Burundi", phone: "257" },
  { code: "BJ", label: "Benin", phone: "229" },
  { code: "BL", label: "Saint Barthelemy", phone: "590" },
  { code: "BM", label: "Bermuda", phone: "1-441" },
  { code: "BN", label: "Brunei Darussalam", phone: "673" },
  { code: "BO", label: "Bolivia", phone: "591" },
  { code: "BR", label: "Brazil", phone: "55" },
  { code: "BS", label: "Bahamas", phone: "1-242" },
  { code: "BT", label: "Bhutan", phone: "975" },
  { code: "BV", label: "Bouvet Island", phone: "47" },
  { code: "BW", label: "Botswana", phone: "267" },
  { code: "BY", label: "Belarus", phone: "375" },
  { code: "BZ", label: "Belize", phone: "501" },
  {
    code: "CA",
    label: "Canada",
    phone: "1",
  },
  {
    code: "CC",
    label: "Cocos (Keeling) Islands",
    phone: "61",
  },
  {
    code: "CD",
    label: "Congo, Democratic Republic of the",
    phone: "243",
  },
  {
    code: "CF",
    label: "Central African Republic",
    phone: "236",
  },
  {
    code: "CG",
    label: "Congo, Republic of the",
    phone: "242",
  },
  { code: "CH", label: "Switzerland", phone: "41" },
  { code: "CI", label: "Cote d'Ivoire", phone: "225" },
  { code: "CK", label: "Cook Islands", phone: "682" },
  { code: "CL", label: "Chile", phone: "56" },
  { code: "CM", label: "Cameroon", phone: "237" },
  { code: "CN", label: "China", phone: "86" },
  { code: "CO", label: "Colombia", phone: "57" },
  { code: "CR", label: "Costa Rica", phone: "506" },
  { code: "CU", label: "Cuba", phone: "53" },
  { code: "CV", label: "Cape Verde", phone: "238" },
  { code: "CW", label: "Curacao", phone: "599" },
  { code: "CX", label: "Christmas Island", phone: "61" },
  { code: "CY", label: "Cyprus", phone: "357" },
  { code: "CZ", label: "Czech Republic", phone: "420" },
  {
    code: "DE",
    label: "Germany",
    phone: "49",
  },
  { code: "DJ", label: "Djibouti", phone: "253" },
  { code: "DK", label: "Denmark", phone: "45" },
  { code: "DM", label: "Dominica", phone: "1-767" },
  {
    code: "DO",
    label: "Dominican Republic",
    phone: "1-809",
  },
  { code: "DZ", label: "Algeria", phone: "213" },
  { code: "EC", label: "Ecuador", phone: "593" },
  { code: "EE", label: "Estonia", phone: "372" },
  { code: "EG", label: "Egypt", phone: "20" },
  { code: "EH", label: "Western Sahara", phone: "212" },
  { code: "ER", label: "Eritrea", phone: "291" },
  { code: "ES", label: "Spain", phone: "34" },
  { code: "ET", label: "Ethiopia", phone: "251" },
  { code: "FI", label: "Finland", phone: "358" },
  { code: "FJ", label: "Fiji", phone: "679" },
  {
    code: "FK",
    label: "Falkland Islands (Malvinas)",
    phone: "500",
  },
  {
    code: "FM",
    label: "Micronesia, Federated States of",
    phone: "691",
  },
  { code: "FO", label: "Faroe Islands", phone: "298" },
  {
    code: "FR",
    label: "France",
    phone: "33",
  },
  { code: "GA", label: "Gabon", phone: "241" },
  { code: "GB", label: "United Kingdom", phone: "44" },
  { code: "GD", label: "Grenada", phone: "1-473" },
  { code: "GE", label: "Georgia", phone: "995" },
  { code: "GF", label: "French Guiana", phone: "594" },
  { code: "GG", label: "Guernsey", phone: "44" },
  { code: "GH", label: "Ghana", phone: "233" },
  { code: "GI", label: "Gibraltar", phone: "350" },
  { code: "GL", label: "Greenland", phone: "299" },
  { code: "GM", label: "Gambia", phone: "220" },
  { code: "GN", label: "Guinea", phone: "224" },
  { code: "GP", label: "Guadeloupe", phone: "590" },
  { code: "GQ", label: "Equatorial Guinea", phone: "240" },
  { code: "GR", label: "Greece", phone: "30" },
  {
    code: "GS",
    label: "South Georgia and the South Sandwich Islands",
    phone: "500",
  },
  { code: "GT", label: "Guatemala", phone: "502" },
  { code: "GU", label: "Guam", phone: "1-671" },
  { code: "GW", label: "Guinea-Bissau", phone: "245" },
  { code: "GY", label: "Guyana", phone: "592" },
  { code: "HK", label: "Hong Kong", phone: "852" },
  {
    code: "HM",
    label: "Heard Island and McDonald Islands",
    phone: "672",
  },
  { code: "HN", label: "Honduras", phone: "504" },
  { code: "HR", label: "Croatia", phone: "385" },
  { code: "HT", label: "Haiti", phone: "509" },
  { code: "HU", label: "Hungary", phone: "36" },
  { code: "ID", label: "Indonesia", phone: "62" },
  { code: "IE", label: "Ireland", phone: "353" },
  { code: "IL", label: "Israel", phone: "972" },
  { code: "IM", label: "Isle of Man", phone: "44" },
  { code: "IN", label: "India", phone: "91" },
  {
    code: "IO",
    label: "British Indian Ocean Territory",
    phone: "246",
  },
  { code: "IQ", label: "Iraq", phone: "964" },
  {
    code: "IR",
    label: "Iran, Islamic Republic of",
    phone: "98",
  },
  { code: "IS", label: "Iceland", phone: "354" },
  { code: "IT", label: "Italy", phone: "39" },
  { code: "JE", label: "Jersey", phone: "44" },
  { code: "JM", label: "Jamaica", phone: "1-876" },
  { code: "JO", label: "Jordan", phone: "962" },
  {
    code: "JP",
    label: "Japan",
    phone: "81",
  },
  { code: "KE", label: "Kenya", phone: "254" },
  { code: "KG", label: "Kyrgyzstan", phone: "996" },
  { code: "KH", label: "Cambodia", phone: "855" },
  { code: "KI", label: "Kiribati", phone: "686" },
  { code: "KM", label: "Comoros", phone: "269" },
  {
    code: "KN",
    label: "Saint Kitts and Nevis",
    phone: "1-869",
  },
  {
    code: "KP",
    label: "Korea, Democratic People's Republic of",
    phone: "850",
  },
  { code: "KR", label: "Korea, Republic of", phone: "82" },
  { code: "KW", label: "Kuwait", phone: "965" },
  { code: "KY", label: "Cayman Islands", phone: "1-345" },
  { code: "KZ", label: "Kazakhstan", phone: "7" },
  {
    code: "LA",
    label: "Lao People's Democratic Republic",
    phone: "856",
  },
  { code: "LB", label: "Lebanon", phone: "961" },
  { code: "LC", label: "Saint Lucia", phone: "1-758" },
  { code: "LI", label: "Liechtenstein", phone: "423" },
  { code: "LK", label: "Sri Lanka", phone: "94" },
  { code: "LR", label: "Liberia", phone: "231" },
  { code: "LS", label: "Lesotho", phone: "266" },
  { code: "LT", label: "Lithuania", phone: "370" },
  { code: "LU", label: "Luxembourg", phone: "352" },
  { code: "LV", label: "Latvia", phone: "371" },
  { code: "LY", label: "Libya", phone: "218" },
  { code: "MA", label: "Morocco", phone: "212" },
  { code: "MC", label: "Monaco", phone: "377" },
  {
    code: "MD",
    label: "Moldova, Republic of",
    phone: "373",
  },
  { code: "ME", label: "Montenegro", phone: "382" },
  {
    code: "MF",
    label: "Saint Martin (French part)",
    phone: "590",
  },
  { code: "MG", label: "Madagascar", phone: "261" },
  { code: "MH", label: "Marshall Islands", phone: "692" },
  {
    code: "MK",
    label: "Macedonia, the Former Yugoslav Republic of",
    phone: "389",
  },
  { code: "ML", label: "Mali", phone: "223" },
  { code: "MM", label: "Myanmar", phone: "95" },
  { code: "MN", label: "Mongolia", phone: "976" },
  { code: "MO", label: "Macao", phone: "853" },
  {
    code: "MP",
    label: "Northern Mariana Islands",
    phone: "1-670",
  },
  { code: "MQ", label: "Martinique", phone: "596" },
  { code: "MR", label: "Mauritania", phone: "222" },
  { code: "MS", label: "Montserrat", phone: "1-664" },
  { code: "MT", label: "Malta", phone: "356" },
  { code: "MU", label: "Mauritius", phone: "230" },
  { code: "MV", label: "Maldives", phone: "960" },
  { code: "MW", label: "Malawi", phone: "265" },
  { code: "MX", label: "Mexico", phone: "52" },
  { code: "MY", label: "Malaysia", phone: "60" },
  { code: "MZ", label: "Mozambique", phone: "258" },
  { code: "NA", label: "Namibia", phone: "264" },
  { code: "NC", label: "New Caledonia", phone: "687" },
  { code: "NE", label: "Niger", phone: "227" },
  { code: "NF", label: "Norfolk Island", phone: "672" },
  { code: "NG", label: "Nigeria", phone: "234" },
  { code: "NI", label: "Nicaragua", phone: "505" },
  { code: "NL", label: "Netherlands", phone: "31" },
  { code: "NO", label: "Norway", phone: "47" },
  { code: "NP", label: "Nepal", phone: "977" },
  { code: "NR", label: "Nauru", phone: "674" },
  { code: "NU", label: "Niue", phone: "683" },
  { code: "NZ", label: "New Zealand", phone: "64" },
  { code: "OM", label: "Oman", phone: "968" },
  { code: "PA", label: "Panama", phone: "507" },
  { code: "PE", label: "Peru", phone: "51" },
  { code: "PF", label: "French Polynesia", phone: "689" },
  { code: "PG", label: "Papua New Guinea", phone: "675" },
  { code: "PH", label: "Philippines", phone: "63" },
  { code: "PK", label: "Pakistan", phone: "92" },
  { code: "PL", label: "Poland", phone: "48" },
  {
    code: "PM",
    label: "Saint Pierre and Miquelon",
    phone: "508",
  },
  { code: "PN", label: "Pitcairn", phone: "870" },
  { code: "PR", label: "Puerto Rico", phone: "1" },
  {
    code: "PS",
    label: "Palestine, State of",
    phone: "970",
  },
  { code: "PT", label: "Portugal", phone: "351" },
  { code: "PW", label: "Palau", phone: "680" },
  { code: "PY", label: "Paraguay", phone: "595" },
  { code: "QA", label: "Qatar", phone: "974" },
  { code: "RE", label: "Reunion", phone: "262" },
  { code: "RO", label: "Romania", phone: "40" },
  { code: "RS", label: "Serbia", phone: "381" },
  { code: "RU", label: "Russian Federation", phone: "7" },
  { code: "RW", label: "Rwanda", phone: "250" },
  { code: "SA", label: "Saudi Arabia", phone: "966" },
  { code: "SB", label: "Solomon Islands", phone: "677" },
  { code: "SC", label: "Seychelles", phone: "248" },
  { code: "SD", label: "Sudan", phone: "249" },
  { code: "SE", label: "Sweden", phone: "46" },
  { code: "SG", label: "Singapore", phone: "65" },
  { code: "SH", label: "Saint Helena", phone: "290" },
  { code: "SI", label: "Slovenia", phone: "386" },
  {
    code: "SJ",
    label: "Svalbard and Jan Mayen",
    phone: "47",
  },
  { code: "SK", label: "Slovakia", phone: "421" },
  { code: "SL", label: "Sierra Leone", phone: "232" },
  { code: "SM", label: "San Marino", phone: "378" },
  { code: "SN", label: "Senegal", phone: "221" },
  { code: "SO", label: "Somalia", phone: "252" },
  { code: "SR", label: "Suriname", phone: "597" },
  { code: "SS", label: "South Sudan", phone: "211" },
  {
    code: "ST",
    label: "Sao Tome and Principe",
    phone: "239",
  },
  { code: "SV", label: "El Salvador", phone: "503" },
  {
    code: "SX",
    label: "Sint Maarten (Dutch part)",
    phone: "1-721",
  },
  {
    code: "SY",
    label: "Syrian Arab Republic",
    phone: "963",
  },
  { code: "SZ", label: "Swaziland", phone: "268" },
  {
    code: "TC",
    label: "Turks and Caicos Islands",
    phone: "1-649",
  },
  { code: "TD", label: "Chad", phone: "235" },
  {
    code: "TF",
    label: "French Southern Territories",
    phone: "262",
  },
  { code: "TG", label: "Togo", phone: "228" },
  { code: "TH", label: "Thailand", phone: "66" },
  { code: "TJ", label: "Tajikistan", phone: "992" },
  { code: "TK", label: "Tokelau", phone: "690" },
  { code: "TL", label: "Timor-Leste", phone: "670" },
  { code: "TM", label: "Turkmenistan", phone: "993" },
  { code: "TN", label: "Tunisia", phone: "216" },
  { code: "TO", label: "Tonga", phone: "676" },
  { code: "TR", label: "Turkey", phone: "90" },
  {
    code: "TT",
    label: "Trinidad and Tobago",
    phone: "1-868",
  },
  { code: "TV", label: "Tuvalu", phone: "688" },
  {
    code: "TW",
    label: "Taiwan",
    phone: "886",
  },
  {
    code: "TZ",
    label: "United Republic of Tanzania",
    phone: "255",
  },
  { code: "UA", label: "Ukraine", phone: "380" },
  { code: "UG", label: "Uganda", phone: "256" },
  {
    code: "US",
    label: "United States",
    phone: "1",
  },
  { code: "UY", label: "Uruguay", phone: "598" },
  { code: "UZ", label: "Uzbekistan", phone: "998" },
  {
    code: "VA",
    label: "Holy See (Vatican City State)",
    phone: "379",
  },
  {
    code: "VC",
    label: "Saint Vincent and the Grenadines",
    phone: "1-784",
  },
  { code: "VE", label: "Venezuela", phone: "58" },
  {
    code: "VG",
    label: "British Virgin Islands",
    phone: "1-284",
  },
  {
    code: "VI",
    label: "US Virgin Islands",
    phone: "1-340",
  },
  { code: "VN", label: "Vietnam", phone: "84" },
  { code: "VU", label: "Vanuatu", phone: "678" },
  { code: "WF", label: "Wallis and Futuna", phone: "681" },
  { code: "WS", label: "Samoa", phone: "685" },
  { code: "XK", label: "Kosovo", phone: "383" },
  { code: "YE", label: "Yemen", phone: "967" },
  { code: "YT", label: "Mayotte", phone: "262" },
  { code: "ZA", label: "South Africa", phone: "27" },
  { code: "ZM", label: "Zambia", phone: "260" },
  { code: "ZW", label: "Zimbabwe", phone: "263" },
];

export const getBrowserRegion = () => {
  const locales = navigator.languages || [navigator.language];

  for (const locale of locales) {
    const parts = locale.split("-");
    if (parts[1]) return parts[1].toUpperCase();
  }

  return "GB";
};

export const getCountryNameFromCode = (code) => {
  if (!code || typeof code !== "string") return null;

  const upperCode = code.toUpperCase();

  const country = COUNTRIES.find((c) => c.code === upperCode);

  return country ? country.label : upperCode;
};

const getRelativeDate = (daysAgo) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split("T")[0];
};

export const demoJournal = {
  movie: [
    {
      journal_id: "demo-movie-2",
      content_type: "movie",
      content_id: null,
      status: "finished",
      date: getRelativeDate(7),
      rating: 4.5,
      reactions: ["fast-paced", "directing", "gripping"],
      notes:
        "Incredible movie, really enjoyed it. Was on the edge of my seat from start to finish. Watched with Zoe and Matt",
      visibility: "everyone",
    },
    {
      journal_id: "demo-movie-1",
      content_type: "movie",
      content_id: null,
      status: "queued",
      date: getRelativeDate(90),
      rating: null,
      reactions: null,
      notes: "Recommended by Archie",
      visibility: "only_me",
    },
  ],
  tvshow: [
    {
      journal_id: "demo-tvshow-3",
      content_type: "tvshow",
      content_id: null,
      status: "finished",
      date: getRelativeDate(1),
      rating: 4.5,
      reactions: ["fast-paced", "writing", "interesting"],
      notes:
        "Incredible, really enjoyed it. Was on the edge of my seat from start to finish. Watched with Zoe and Matt",
      visibility: "everyone",
    },
    {
      journal_id: "demo-tvshow-2",
      content_type: "tvshow",
      content_id: null,
      status: "started",
      date: getRelativeDate(30),
      rating: null,
      reactions: null,
      notes: "One episode in and loving it already",
      visibility: "everyone",
    },
    {
      journal_id: "demo-tvshow-1",
      content_type: "tvshow",
      content_id: null,
      status: "queued",
      date: getRelativeDate(90),
      rating: null,
      reactions: null,
      notes: "Everyone's been talking about this, must give it a watch",
      visibility: "only_me",
    },
  ],
  book: [
    {
      journal_id: "demo-book-3",
      content_type: "book",
      content_id: null,
      status: "finished",
      date: getRelativeDate(1),
      rating: 4.5,
      reactions: ["slow-paced", "themes & ideas"],
      notes:
        "Incredible book. Love the author, can't wait to read more books by them",
      visibility: "everyone",
    },
    {
      journal_id: "demo-book-2",
      content_type: "book",
      content_id: null,
      status: "started",
      date: getRelativeDate(30),
      rating: null,
      reactions: null,
      notes: null,
    },

    {
      journal_id: "demo-book-1",
      content_type: "book",
      content_id: null,
      status: "queued",
      date: getRelativeDate(90),
      rating: null,
      reactions: null,
      notes: "Recommended by Archie",
      visibility: "only_me",
    },
  ],
};

// Starter collection shown on the onboarding page, keyed by content preference.
// Same IDs in dev and prod — the dev DB is a copy of prod.
export const ONBOARDING_COLLECTION_IDS = {
  movie: 3259,
  tvshow: 3902,
  book: 5219,
};
// movie: NYT's 100 Best Movies of the 21st Century
// tvshow: The Guardian's 100 Best TV Shows of the 21st Century
// book: 100 Most Popular Books on Goodreads
